import { useSuggestAnalyticsGetApi } from '@/common/api/modules/searchSuggest'
import { searchSuggestDictionaryPath } from '@/common/app/path'
import { useSiteId } from '@/common/app/site'

import Button, { ButtonColor } from '@/components/view/atoms/button/Button'
import BarChart from '@/components/view/atoms/chart/BarChart'
import ChartTitle from '@/components/view/atoms/chart/ChartTitle'
import MultiLineChart from '@/components/view/atoms/chart/MultiLineChart'
import Heading from '@/components/view/atoms/heading/Heading'
import Text, { TextColor } from '@/components/view/atoms/text/Text'
import Area from '@/components/view/layouts/area/Area'
import AreaContent from '@/components/view/layouts/area/AreaContent'
import ChartWrapper from '@/components/view/layouts/chart/ChartWrapper'
import Content from '@/components/view/layouts/page/Content'
import Main, { MainLayoutType } from '@/components/view/layouts/page/Main'
import PageListLoading from '@/components/view/molecules/loading/PageListLoading'
import PageTitle from '@/components/view/molecules/title/PageTitle'

import DetailPageLoader from '@/components/container/DetailPageLoader'

interface KpiCardProps {
  label: string
  value: number
  delta: number
  /** 増加が望ましい指標は positiveUp=true。0件率のように減少が望ましい指標は false。 */
  positiveUp?: boolean
}

const KpiCard = ({ label, value, delta, positiveUp = true }: KpiCardProps) => {
  const good = positiveUp ? delta >= 0 : delta <= 0
  const deltaColor =
    delta === 0 ? TextColor.SECONDARY : good ? TextColor.SUCCESS : TextColor.CRITICAL
  const sign = delta > 0 ? '+' : ''
  return (
    <div className='border-border-input bg-surface flex flex-col gap-1 rounded-xl border p-4'>
      <Text size={12} color={TextColor.SECONDARY}>
        {label}
      </Text>
      <Text size={24} weight='bold'>
        {value.toFixed(1)}%
      </Text>
      <Text size={12} color={deltaColor}>
        {sign}
        {delta.toFixed(1)}pt vs 前期間
      </Text>
    </div>
  )
}

const SearchSuggestAnalyticsPage = () => {
  const siteId = useSiteId()
  const { data, error, isLoading } = useSuggestAnalyticsGetApi({ site_id: siteId, days: 14 })

  return (
    <DetailPageLoader error={error} data={!isLoading && data}>
      {data && (
        <Main type={MainLayoutType.MEDIUM}>
          <PageTitle
            title='ダッシュボード'
            description='サジェスト利用率・経由CTR・経由CVR・0件ヒット率を計測し、改善につなげます。'
            rightNode={
              <Button color={ButtonColor.SECONDARY} link={searchSuggestDictionaryPath(siteId)}>
                0件キーワードを改善する
              </Button>
            }
          />
          <Content>
            <div className='grid grid-cols-2 gap-3 md:grid-cols-4'>
              <KpiCard
                label='サジェスト利用率'
                value={data.utilization}
                delta={data.utilization_delta}
              />
              <KpiCard label='経由CTR' value={data.ctr} delta={data.ctr_delta} />
              <KpiCard label='経由CVR' value={data.cvr} delta={data.cvr_delta} />
              <KpiCard
                label='0件ヒット率'
                value={data.zero_rate}
                delta={data.zero_rate_delta}
                positiveUp={false}
              />
            </div>

            <Area>
              <Heading>0件ヒット率 / 経由CVR の推移（直近{data.period_days}日）</Heading>
              <AreaContent>
                <ChartWrapper>
                  <MultiLineChart
                    labels={data.trend.map((t) => t.date)}
                    items={[
                      { title: '0件率', values: data.trend.map((t) => t.zero_rate) },
                      { title: 'CVR', values: data.trend.map((t) => t.cvr) }
                    ]}
                    unit='%'
                  />
                </ChartWrapper>
              </AreaContent>
            </Area>

            <Area>
              <Heading>人気サジェスト語ランキング（クリック数）</Heading>
              <AreaContent>
                {data.ranking.length > 0 ? (
                  <ChartWrapper>
                    <ChartTitle>クリック数</ChartTitle>
                    <BarChart
                      labels={data.ranking.map((r) => r.term)}
                      items={data.ranking.map((r) => ({ title: r.term, values: [r.count] }))}
                      single
                    />
                  </ChartWrapper>
                ) : (
                  <Text color={TextColor.SECONDARY}>クリックデータがまだありません。</Text>
                )}
              </AreaContent>
            </Area>
          </Content>
        </Main>
      )}
      {isLoading && <PageListLoading />}
    </DetailPageLoader>
  )
}

export default SearchSuggestAnalyticsPage
